// Quote editor — clone of InvoiceEdit but with quote-specific status workflow.
// Quotes don't post to the ledger (no financial impact until converted).
// "Convert to invoice" creates a linked invoice from the quote's lines.

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Trash2, Plus, Send, Save, ArrowLeft, Printer, Share2, ArrowRight, Check, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { addDaysISO, formatDate, formatMoney, todayISO } from '../lib/format';
import { calcLine, postInvoice, r2 } from '../lib/posting';
import type { Account, Contact, Quote, QuoteLine, TaxCode, Service, Invoice, InvoiceLine } from '../lib/types';


interface Line { id: string; description: string; qty: number; unit_price: number; tax_code_id: string | null; account_id: string | null; position: number; }
const newLine = (pos: number): Line => ({ id: 'tmp-' + Math.random().toString(36).slice(2), description: '', qty: 1, unit_price: 0, tax_code_id: null, account_id: null, position: pos });

export default function QuoteEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentOrg } = useOrg();
  const isNew = !id;

  const [quote, setQuote] = useState<Quote | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [taxCodes, setTaxCodes] = useState<TaxCode[]>([]);
  const [services, setServices] = useState<Service[]>([]);

  const [contactId, setContactId] = useState('');
  const [number, setNumber] = useState('');
  const [issueDate, setIssueDate] = useState(todayISO());
  const [expiryDate, setExpiryDate] = useState(addDaysISO(todayISO(), 30));
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('');
  const [currency, setCurrency] = useState('ZAR');
  const [lines, setLines] = useState<Line[]>([newLine(0)]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    if (!currentOrg) return;
    (async () => {
      const [cs, accs, tcs, svcs] = await Promise.all([
        supabase.from('dodobooks_contacts').select('*').eq('org_id', currentOrg.id).eq('is_customer', true).eq('is_archived', false).order('name'),
        supabase.from('dodobooks_accounts').select('*').eq('org_id', currentOrg.id).eq('is_archived', false).order('code'),
        supabase.from('dodobooks_tax_codes').select('*').eq('org_id', currentOrg.id).eq('is_active', true).order('code'),
        supabase.from('dodobooks_services').select('*').eq('org_id', currentOrg.id).eq('is_archived', false).eq('is_purchase', false).order('name'),
      ]);
      setContacts((cs.data ?? []) as Contact[]);
      setAccounts((accs.data ?? []) as Account[]);
      setTaxCodes((tcs.data ?? []) as TaxCode[]);
      setServices((svcs.data ?? []) as Service[]);

      const defTax = (tcs.data ?? []).find((t: any) => t.code === (currentOrg.is_vat_registered ? 'STD' : 'OUT'));
      const defAcc = (accs.data ?? []).find((a: any) => a.code === '4000');
      if (isNew) {
        setLines([{ ...newLine(0), tax_code_id: defTax?.id ?? null, account_id: defAcc?.id ?? null }]);
        const { count } = await supabase.from('dodobooks_quotes').select('id', { count: 'exact', head: true }).eq('org_id', currentOrg.id);
        setNumber('Q-' + new Date().getFullYear() + '-' + String((count ?? 0) + 1).padStart(4, '0'));
      }
    })();
  }, [currentOrg, isNew]);

  useEffect(() => {
    if (!id || !currentOrg) return;
    (async () => {
      const { data: q } = await supabase.from('dodobooks_quotes').select('*').eq('id', id).maybeSingle();
      if (q) {
        setQuote(q as Quote);
        const qq = q as any;
        setContactId(qq.contact_id);
        setNumber(qq.number);
        setIssueDate(qq.issue_date);
        setExpiryDate(qq.expiry_date);
        setReference(qq.reference ?? '');
        setNotes(qq.notes ?? '');
        setTerms(qq.terms ?? '');
        setCurrency(qq.currency);
      }
      const { data: ls } = await supabase.from('dodobooks_quote_lines').select('*').eq('quote_id', id).order('position');
      if (ls?.length) {
        setLines(ls.map((l: any) => ({
          id: l.id, description: l.description, qty: Number(l.qty), unit_price: Number(l.unit_price),
          tax_code_id: l.tax_code_id, account_id: l.account_id, position: l.position,
        })));
      }
    })();
  }, [id, currentOrg]);

  const status = quote?.status ?? 'draft';
  const readOnly = status !== 'draft';
  const canSendOrConvert = status === 'sent' || status === 'accepted';

  const totals = useMemo(() => {
    let subtotal = 0, tax = 0;
    for (const l of lines) {
      const tc = taxCodes.find((t) => t.id === l.tax_code_id);
      const rate = tc?.rate ?? 0;
      const { net, tax: lineTax } = calcLine(l.qty || 0, l.unit_price || 0, rate);
      subtotal += net; tax += lineTax;
    }
    return { subtotal: r2(subtotal), tax: r2(tax), total: r2(subtotal + tax) };
  }, [lines, taxCodes]);

  const updateLine = (i: number, patch: Partial<Line>) => setLines((p) => p.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const removeLine = (i: number) => setLines((p) => p.length === 1 ? p : p.filter((_, idx) => idx !== i));
  const addLine = () => { const last = lines[lines.length - 1]; setLines((p) => [...p, { ...newLine(p.length), tax_code_id: last?.tax_code_id ?? null, account_id: last?.account_id ?? null }]); };

  function pickService(i: number, serviceId: string) {
    if (!serviceId) return;
    const sv = services.find((x) => x.id === serviceId);
    if (!sv) return;
    updateLine(i, { description: sv.description || sv.name, qty: 1, unit_price: Number(sv.default_price) || 0, account_id: sv.default_account_id, tax_code_id: sv.default_tax_code_id });
  }


  async function persist(): Promise<string | null> {
    if (!currentOrg) return null;
    if (!contactId) { setErr('Pick a customer first.'); return null; }
    const payload = {
      org_id: currentOrg.id, contact_id: contactId, number, issue_date: issueDate, expiry_date: expiryDate,
      reference: reference || null, status: status === 'draft' ? 'draft' : status, currency,
      subtotal: totals.subtotal, tax_total: totals.tax, total: totals.total,
      notes: notes || null, terms: terms || null,
    };
    let qId = id ?? null;
    if (qId) {
      const { error } = await supabase.from('dodobooks_quotes').update(payload).eq('id', qId);
      if (error) { setErr(error.message); return null; }
    } else {
      const { data, error } = await supabase.from('dodobooks_quotes').insert(payload).select('id').single();
      if (error || !data) { setErr(error?.message ?? 'Insert failed'); return null; }
      qId = data.id;
    }
    await supabase.from('dodobooks_quote_lines').delete().eq('quote_id', qId);
    const lineRows = lines.map((l, i) => {
      const tc = taxCodes.find((t) => t.id === l.tax_code_id);
      const { total: lineTotal } = calcLine(l.qty || 0, l.unit_price || 0, tc?.rate ?? 0);
      return { quote_id: qId, description: l.description, qty: l.qty || 0, unit_price: l.unit_price || 0, tax_code_id: l.tax_code_id, account_id: l.account_id, line_total: r2(lineTotal), position: i };
    });
    if (lineRows.length > 0) {
      const { error } = await supabase.from('dodobooks_quote_lines').insert(lineRows);
      if (error) { setErr(error.message); return null; }
    }
    return qId;
  }

  async function onSave() {
    setBusy(true); setErr(null); setMsg(null);
    const newId = await persist();
    setBusy(false);
    if (newId) { setMsg('Draft saved.'); if (!id) navigate('/quotes/' + newId, { replace: true }); }
  }

  async function onSend() {
    setBusy(true); setErr(null); setMsg(null);
    const newId = await persist();
    if (!newId) { setBusy(false); return; }
    const { error } = await supabase.from('dodobooks_quotes').update({ status: 'sent' }).eq('id', newId);
    setBusy(false);
    if (error) setErr(error.message);
    else {
      setMsg('Quote sent. Share the link with your customer.');
      const { data } = await supabase.from('dodobooks_quotes').select('*').eq('id', newId).maybeSingle();
      setQuote(data as Quote);
      if (!id) navigate('/quotes/' + newId, { replace: true });
    }
  }

  async function convertToInvoice() {
    if (!currentOrg || !quote) return;
    if (!confirm(`Convert quote ${quote.number} to an invoice? This will create a new invoice with the same lines and mark the quote as converted.`)) return;
    setConverting(true); setErr(null); setMsg(null);

    const { count } = await supabase.from('dodobooks_invoices').select('id', { count: 'exact', head: true }).eq('org_id', currentOrg.id);
    const invNum = 'INV-' + new Date().getFullYear() + '-' + String((count ?? 0) + 1).padStart(4, '0');

    const { data: inv, error: ie } = await supabase.from('dodobooks_invoices').insert({
      org_id: currentOrg.id, contact_id: quote.contact_id, number: invNum,
      issue_date: todayISO(), due_date: addDaysISO(todayISO(), 30),
      reference: 'Converted from quote ' + quote.number,
      status: 'draft', currency: quote.currency,
      subtotal: 0, tax_total: 0, total: 0, amount_paid: 0,
      kind: 'invoice', notes: quote.notes ?? null,
    }).select('*').single();
    if (ie || !inv) { setConverting(false); setErr(ie?.message ?? 'Could not create invoice'); return; }

    const invLines = lines.map((l, i) => {
      const tc = taxCodes.find((t) => t.id === l.tax_code_id);
      const { total: lineTotal } = calcLine(l.qty || 0, l.unit_price || 0, tc?.rate ?? 0);
      return { invoice_id: (inv as any).id, description: l.description, qty: l.qty || 0, unit_price: l.unit_price || 0, tax_code_id: l.tax_code_id, account_id: l.account_id, line_total: r2(lineTotal), position: i };
    });
    if (invLines.length > 0) await supabase.from('dodobooks_invoice_lines').insert(invLines);

    // Post the invoice immediately so it hits the ledger.
    const { data: invFresh } = await supabase.from('dodobooks_invoices').select('*').eq('id', (inv as any).id).single();
    const { data: invLineRows } = await supabase.from('dodobooks_invoice_lines').select('*').eq('invoice_id', (inv as any).id).order('position');
    const result = await postInvoice({
      org_id: currentOrg.id,
      invoice: invFresh as Invoice,
      lines: (invLineRows ?? []) as InvoiceLine[],
      accounts, taxCodes,
    });
    if (result.error) { setConverting(false); setErr('Invoice created but posting failed: ' + result.error); return; }

    // Mark quote as converted, link to new invoice
    await supabase.from('dodobooks_quotes').update({
      status: 'converted',
      converted_to_invoice_id: (inv as any).id,
      updated_at: new Date().toISOString(),
    }).eq('id', quote.id);

    setConverting(false);
    setMsg('Converted to invoice ' + invNum + '.');
    setTimeout(() => navigate('/invoices/' + (inv as any).id), 800);
  }

  const publicUrl = quote ? window.location.origin + '/app/p/quote/' + quote.public_token : '';

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Link to="/quotes" className="btn btn-ghost"><ArrowLeft size={14} /></Link>
          <div>
            <h1 className="h-page">{number || 'New quote'}</h1>
            <p className="text-text-dim text-sm mt-1">
              {readOnly ? (
                <span className={'pill ' + (status === 'accepted' || status === 'converted' ? 'pill-paid' : status === 'sent' ? 'pill-sent' : status === 'expired' || status === 'declined' ? 'pill-overdue' : 'pill-draft')}>{status}</span>
              ) : 'Editing draft'}
              {quote?.accepted_at && <span className="ml-2 text-emerald text-xs">Accepted by {quote.accepted_by_name} on {formatDate(quote.accepted_at)}</span>}
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!readOnly && (
            <>
              <button onClick={onSave} disabled={busy} className="btn btn-secondary disabled:opacity-50"><Save size={14} /> Save draft</button>
              <button onClick={onSend} disabled={busy} className="btn btn-primary disabled:opacity-50"><Send size={14} /> Send quote</button>
            </>
          )}
          {readOnly && quote?.public_token && (
            <button
              type="button"
              onClick={() => window.open(`/app/p/quote/${quote.public_token}`, '_blank', 'noopener')}
              className="btn btn-secondary"
              title="Opens the branded customer-facing quote — uses your saved template"
            >
              <Printer size={14} /> View / Print
            </button>
          )}
          {canSendOrConvert && quote && (
            <a href={publicUrl} target="_blank" rel="noopener" className="btn btn-secondary"><Share2 size={14} /> Public link</a>
          )}
          {status === 'accepted' && !quote?.converted_to_invoice_id && (
            <button onClick={convertToInvoice} disabled={converting} className="btn btn-primary disabled:opacity-50">
              <ArrowRight size={14} /> {converting ? 'Converting…' : 'Convert to invoice'}
            </button>
          )}
          {quote?.converted_to_invoice_id && (
            <Link to={'/invoices/' + quote.converted_to_invoice_id} className="btn btn-secondary"><Check size={14} /> View invoice</Link>
          )}
        </div>
      </header>

      {msg && <div className="text-sm text-emerald bg-emerald/10 border border-emerald/30 px-3 py-2 rounded-md">{msg}</div>}
      {err && <div className="text-sm text-rust bg-rust/10 border border-rust/30 px-3 py-2 rounded-md">{err}</div>}

      <div className="card grid grid-cols-2 gap-4">
        <div className="field col-span-2">
          <label>Customer *</label>
          <select disabled={readOnly} required value={contactId} onChange={(e) => setContactId(e.target.value)}>
            <option value="">— pick a customer —</option>
            {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="field"><label>Quote number</label><input disabled={readOnly} value={number} onChange={(e) => setNumber(e.target.value)} /></div>
        <div className="field"><label>Reference</label><input disabled={readOnly} value={reference} onChange={(e) => setReference(e.target.value)} placeholder="PO / lead ID, etc." /></div>
        <div className="field"><label>Issue date</label><input disabled={readOnly} type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} /></div>
        <div className="field"><label>Valid until</label><input disabled={readOnly} type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} /></div>
        <div className="field"><label>Currency</label>
          <select disabled={readOnly} value={currency} onChange={(e) => setCurrency(e.target.value)}>
            <option>ZAR</option><option>USD</option><option>EUR</option><option>GBP</option>
          </select>
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <table>
          <thead>
            <tr>
              <th className="px-4">Description</th><th className="w-20">Qty</th><th className="w-32">Unit price</th>
              <th className="w-48">Account</th><th className="w-32">Tax</th><th className="w-32 text-right">Line total</th><th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              const tc = taxCodes.find((t) => t.id === l.tax_code_id);
              const { total: lineTotal } = calcLine(l.qty || 0, l.unit_price || 0, tc?.rate ?? 0);
              return (
                <tr key={l.id}>
                  <td className="px-4">
                    {!readOnly && services.length > 0 && (
                      <select value="" onChange={(e) => { pickService(i, e.target.value); e.currentTarget.value = ''; }} className="w-full text-[11px] mb-1 bg-emerald/5 border border-emerald/20" title="Pick a service to auto-fill">
                        <option value="">＋ Pick a service…</option>
                        {services.map((sv) => <option key={sv.id} value={sv.id}>{sv.name} — R{Number(sv.default_price).toFixed(2)}</option>)}
                      </select>
                    )}
                    <input disabled={readOnly} value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })} placeholder="Item or service…" className="w-full" />
                  </td>
                  <td><input disabled={readOnly} type="number" step="0.01" value={l.qty} onChange={(e) => updateLine(i, { qty: Number(e.target.value) })} className="w-full" /></td>
                  <td><input disabled={readOnly} type="number" step="0.01" value={l.unit_price} onChange={(e) => updateLine(i, { unit_price: Number(e.target.value) })} className="w-full" /></td>
                  <td>
                    <select disabled={readOnly} value={l.account_id ?? ''} onChange={(e) => updateLine(i, { account_id: e.target.value || null })} className="w-full">
                      <option value="">— pick account —</option>
                      {accounts.filter((a) => a.account_type === 'revenue').map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
                    </select>
                  </td>
                  <td>
                    <select disabled={readOnly} value={l.tax_code_id ?? ''} onChange={(e) => updateLine(i, { tax_code_id: e.target.value || null })} className="w-full">
                      <option value="">No VAT</option>
                      {taxCodes.filter((t) => t.is_output).map((t) => <option key={t.id} value={t.id}>{t.code} ({(t.rate * 100).toFixed(0)}%)</option>)}
                    </select>
                  </td>
                  <td className="text-right num">{formatMoney(lineTotal, currency)}</td>
                  <td>{!readOnly && (<button type="button" onClick={() => removeLine(i)} disabled={lines.length === 1} className="text-text-mute hover:text-rust disabled:opacity-30"><Trash2 size={14} /></button>)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!readOnly && (<div className="border-t border-line p-3"><button type="button" onClick={addLine} className="btn btn-ghost"><Plus size={14} /> Add line</button></div>)}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="card">
          <label className="block mb-1">Notes (top of quote)</label>
          <textarea disabled={readOnly} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full" placeholder="Visible to your customer." />
          <label className="block mb-1 mt-3">Terms (under totals)</label>
          <textarea disabled={readOnly} rows={3} value={terms} onChange={(e) => setTerms(e.target.value)} className="w-full" placeholder="e.g. Quote valid for 30 days. 50% deposit on acceptance." />
        </div>
        <div className="card">
          <div className="flex justify-between py-1.5 text-sm text-text-dim"><span>Subtotal</span><span className="num text-text">{formatMoney(totals.subtotal, currency)}</span></div>
          <div className="flex justify-between py-1.5 text-sm text-text-dim border-b border-line"><span>VAT</span><span className="num text-text">{formatMoney(totals.tax, currency)}</span></div>
          <div className="flex justify-between py-2 font-serif text-lg"><span>Total</span><span className="num text-emerald">{formatMoney(totals.total, currency)}</span></div>
          {quote?.public_token && (
            <div className="mt-4 pt-3 border-t border-line">
              <div className="text-[10px] font-mono uppercase tracking-widest text-text-mute mb-1">Public link</div>
              <input readOnly value={publicUrl} className="text-xs w-full" onFocus={(e) => e.currentTarget.select()} />
            </div>
          )}
        </div>
      </div>

      
    </div>
  );
}
